import { supabase } from './supabase/client';
import { resolveScopedStudentIds } from './student.service';
import {
  StudentBehaviour,
  CreateStudentBehaviourDTO,
  UpdateStudentBehaviourDTO,
  StudentBehaviourFilters,
  StudentBehaviourListResponse,
} from 'src/types/behaviour.types';

const TABLE_NAME = 'student_behaviours';

function calculateBehaviorCounts(items: any[]): { positive: number; negative: number } {
  let positive = 0;
  let negative = 0;

  items.forEach((item) => {
    const type = item.behaviour_type?.type;
    if (type === 'highlight' || type === 'positive') {
      positive += 1;
    } else if (type === 'challenge' || type === 'negative') {
      negative += 1;
    }
  });

  return { positive, negative };
}

import { resolveAssignedStudentIdsForTeacher } from './student.service';

/**
 * Service for managing student behaviours
 */
export const studentBehaviourService = {
  /**
   * Get student behaviours with filters and pagination
   */
  async getStudentBehaviours({
    studentId,
    teacherId,
    teacherUserId,
    behaviourTypeId,
    search,
    page = 1,
    limit = 10,
  }: StudentBehaviourFilters = {}): Promise<StudentBehaviourListResponse> {
    try {
      const assignedStudentIds = await resolveAssignedStudentIdsForTeacher({ teacherId, teacherUserId });
      if (assignedStudentIds !== null && assignedStudentIds.length === 0) {
        return { data: [], total: 0, page, limit };
      }

      let query = supabase.from(TABLE_NAME).select(
        `
          *,
          behaviour_types (
            title,
            type,
            points
          ),
          creator:users!student_behaviours_createdBy_fkey(id, firstname, lastname, email, teachers(id)),
          student: students (
            *,
            user: users (
              firstname,
              lastname,
              email
            )
          )
        `,
        { count: 'exact' }
      );

      query = query.is('deletedAt', null);

      if (assignedStudentIds !== null) {
        query = query.in('studentId', assignedStudentIds);
      }

      if (studentId) {
        query = query.eq('studentId', studentId);
      }

      if (behaviourTypeId) {
        query = query.eq('behaviourTypeId', behaviourTypeId);
      }

      if (search && !studentId) {
        query = query.or(
          `user.firstname.ilike.%${search}%,user.lastname.ilike.%${search}%,user.email.ilike.%${search}%`,
          {
            foreignTable: 'users',
          }
        );
      }

      if (search && studentId) {
        query = query.or(`title.ilike.%${search}%`, {
          foreignTable: 'behaviour_types',
        });
      }

      // Add pagination
      const from = (page - 1) * limit;
      const to = from + limit - 1;
      query = query.range(from, to).order('createdAt', { ascending: false });

      const { data, error, count } = await query;

      if (error) {
        console.error('Query error:', error);
        throw error;
      }

      return {
        data: data as StudentBehaviour[],
        total: count || 0,
        page,
        limit,
      };
    } catch (error) {
      console.error('[StudentBehaviourService.getStudentBehaviours]:', error);
      throw error;
    }
  },

  /**
   * Get a single student behaviour by ID
   */
  async getStudentBehaviourById(id: string): Promise<StudentBehaviour | null> {
    try {
      const { data, error } = await supabase
        .from(TABLE_NAME)
        .select(
          `
          *,
          behaviour_types (
            title,
            type,
            points
          ),
          student: students (
            *,
            user: users (
              firstname,
              lastname,
              email
            )
          )
        `
        )
        .eq('id', id)
        .is('deletedAt', null)
        .single();

      if (error) {
        console.error('Query error:', error);
        throw error;
      }

      return data as StudentBehaviour;
    } catch (error) {
      console.error('[StudentBehaviourService.getStudentBehaviourById]:', error);
      throw error;
    }
  },

  /**
   * Create a new student behaviour
   */
  async createStudentBehaviour(dto: CreateStudentBehaviourDTO): Promise<StudentBehaviour> {
    try {
      const { data, error } = await supabase
        .from(TABLE_NAME)
        .insert({
          studentId: dto.studentId,
          behaviourTypeId: dto.behaviourTypeId,
          notes: dto.notes,
          points: dto.points || 0,
          createdBy: dto.createdBy,
          extendedProperties: dto.extendedProperties || {},
        })
        .select(
          `
          *,
          behaviour_types (
            title,
            type,
            points
          ),
          student: students (
            *,
            user: users (
              firstname,
              lastname,
              email
            )
          )
        `
        )
        .single();

      if (error) {
        console.error('Insert error:', error);
        throw error;
      }

      return data as StudentBehaviour;
    } catch (error) {
      console.error('[StudentBehaviourService.createStudentBehaviour]:', error);
      throw error;
    }
  },

  /**
   * Update a student behaviour
   */
  async updateStudentBehaviour(id: string, dto: UpdateStudentBehaviourDTO): Promise<StudentBehaviour> {
    try {
      const { data, error } = await supabase
        .from(TABLE_NAME)
        .update({
          studentId: dto.studentId,
          behaviourTypeId: dto.behaviourTypeId,
          notes: dto.notes,
          points: dto.points,
          extendedProperties: dto.extendedProperties,
        })
        .eq('id', id)
        .is('deletedAt', null)
        .select(
          `
          *,
          behaviour_types (
            title,
            type,
            points
          ),
          student: students (
            *,
            user: users (
              firstname,
              lastname,
              email
            )
          )
        `
        )
        .single();

      if (error) {
        console.error('Update error:', error);
        throw error;
      }

      return data as StudentBehaviour;
    } catch (error) {
      console.error('[StudentBehaviourService.updateStudentBehaviour]:', error);
      throw error;
    }
  },

  /**
   * Delete a student behaviour (soft delete)
   */
  async deleteStudentBehaviour(id: string): Promise<void> {
    try {
      const { error } = await supabase
        .from(TABLE_NAME)
        .update({ deletedAt: new Date().toISOString() })
        .eq('id', id);

      if (error) {
        console.error('Delete error:', error);
        throw error;
      }
    } catch (error) {
      console.error('[StudentBehaviourService.deleteStudentBehaviour]:', error);
      throw error;
    }
  },

  async getPlatformBehaviorCount(filters?: {
    teacherId?: string;
    teacherUserId?: string;
    cohortId?: string;
  }): Promise<{ positive: number; negative: number }> {
    try {
      const scopedStudentIds = await resolveScopedStudentIds(filters);
      if (scopedStudentIds !== null && scopedStudentIds.length === 0) {
        return { positive: 0, negative: 0 };
      }

      let query = supabase
        .from('student_behaviours')
        .select(
          `
            id,
            behaviour_type:behaviour_types!inner(type),
            student:students!inner(
              id,
              user:users!inner(
                id,
                deletedat
              )
            )
          `
        )
        .is('deletedAt', null)
        .is('student.user.deletedat', null);

      if (scopedStudentIds !== null) {
        query = query.in('studentId', scopedStudentIds);
      }

      const { data, error } = await query;
      if (error) throw error;

      return calculateBehaviorCounts(data || []);
    } catch (error) {
      console.error('[StudentBehaviourService.getPlatformBehaviorCount]:', error);
      throw error;
    }
  },

  async getStreetCredMetrics(params?: {
    studentId?: string;
    teacherId?: string;
    teacherUserId?: string;
  }): Promise<{
    totalBehaviors: number;
    positiveCount: number;
    negativeCount: number;
    netPoints: number;
    positiveRatio: number;
    totalTypes: number;
  }> {
    try {
      const assignedStudentIds = await resolveAssignedStudentIdsForTeacher({
        teacherId: params?.teacherId,
        teacherUserId: params?.teacherUserId,
      });

      const { count: typesCount } = await supabase
        .from('behaviour_types')
        .select('*', { count: 'exact', head: true })
        .is('deletedat', null);

      if (assignedStudentIds !== null && assignedStudentIds.length === 0) {
        return {
          totalBehaviors: 0,
          positiveCount: 0,
          negativeCount: 0,
          netPoints: 0,
          positiveRatio: 100,
          totalTypes: typesCount || 0,
        };
      }

      let query = supabase
        .from(TABLE_NAME)
        .select(
          `
          id,
          points,
          behaviour_types (
            type,
            points
          )
        `
        )
        .is('deletedAt', null);

      if (assignedStudentIds !== null) {
        query = query.in('studentId', assignedStudentIds);
      }

      if (params?.studentId) {
        query = query.eq('studentId', params.studentId);
      }

      const { data: behaviours, error: bError } = await query;

      if (bError) throw bError;

      const items = (behaviours as any[]) || [];
      const totalBehaviors = items.length;
      let positiveCount = 0;
      let negativeCount = 0;
      let netPoints = 0;

      items.forEach((item) => {
        const type = item?.behaviour_types?.type;
        const pts = Number(item?.points ?? item?.behaviour_types?.points ?? 0);
        if (type === 'highlight' || pts > 0) {
          positiveCount += 1;
        } else if (type === 'challenge' || pts < 0) {
          negativeCount += 1;
        }
        netPoints += pts;
      });

      const positiveRatio = totalBehaviors > 0 ? Math.round((positiveCount / totalBehaviors) * 100) : 100;

      return {
        totalBehaviors,
        positiveCount,
        negativeCount,
        netPoints,
        positiveRatio,
        totalTypes: typesCount || 0,
      };
    } catch (error) {
      console.error('[StudentBehaviourService.getStreetCredMetrics]:', error);
      return {
        totalBehaviors: 0,
        positiveCount: 0,
        negativeCount: 0,
        netPoints: 0,
        positiveRatio: 100,
        totalTypes: 0,
      };
    }
  },
};
